import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Accordion } from 'react-bootstrap';
import {
  AppCard,
  AppFileUploader,
  AppModal,
  AppNoInfo,
  AppRemixIcon,
  AppTextArea,
} from '@/layout/AppComponents';
import axios from 'axios';
import ReactDOMServer from 'react-dom/server';
import * as Yup from 'yup';
import Sidebar from '../../../layout/Sidebar/Sidebar';
import AppLink from '../../../routing/helpers/AppLink';
import Translator from '../../../layout/components/helpers/Translator';
import BreadcrumbHandler from '../../../layout/components/breadcrumb/BreadcrumbHandler';
import useIsMounted from '../../../layout/components/hooks/useIsMounted';
import { axiosPath, path } from '../../../routing/helpers/router';
import { Formik } from 'formik';
import SpinnerLoader from '../../../layout/components/helpers/SpinnerLoader';
import minions from '../../../minions/minions';

export default function UserQueryMessagesScreen() {
  const { moduleBridgeHex, plantHex, userQueryThreadHEX } = useParams();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const [userQueryThread, setUserQueryThread] = useState(null);
  const [authUser, setAuthUser] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const client = useSelector((state) => state.auth.user.client);
  const validationSchema = Yup.object().shape({
    message: Yup.string(),
  });

  const loadUserQuery = useCallback(async () => {
    await axios
      .post(axiosPath('api_r_user_userQuery_messages'), {
        moduleBridgeHex,
        plantHex,
        userQueryThreadHEX,
      })
      .then((r) => {
        if (isMounted.current) {
          setUserQueryThread(r.data.userQueryThread);
          setAuthUser(r.data.authUser);
        }
      })
      .catch((err) => {
        if (err.response.status === 428)
          navigate(
            path({
              name: 'user_home',
            }),
          );
      });
  }, [moduleBridgeHex, plantHex]);

  useEffect(() => {
    if (moduleBridgeHex && plantHex && userQueryThreadHEX) {
      loadUserQuery();
    }
  }, [moduleBridgeHex, plantHex]);

  const handleReply = useCallback(
    async (payload, isContactAdmin = false) => {
      await axios
        .post(
          axiosPath('api_r_user_userQuery_messages_set'),
          minions.forms.toFormData({
            moduleBridgeHex,
            plantHex,
            userQueryThreadHEX,
            ...payload,
            isContactAdmin,
          }),
          { headers: { 'Content-Type': 'multipart/form-data' } }
        )
        .then((r) => {
          if (isMounted.current) {
            loadUserQuery();
            setShowEditModal(false);
          }
        })
        .catch((err) => {
          if (err.response.status === 428)
            navigate(
              path({
                name: 'user_home',
              }),
            );
        });
    },
    [moduleBridgeHex, plantHex]
  );

  const handleDownloadFile = useCallback(
    async (userQueryMessageID, key, fileName) => {
      axios
        .post(axiosPath('api_r_user_userQuery_messages_file_download'), {
          userQueryMessageID,
          key,
          fileName,
        })
        .then((r) => {
          if (isMounted.current) {
            window.open(r.data, '_blank');
          }
        })
        .catch((err) => {
          if (err.response.status === 428)
            navigate(
              path({
                name: 'user_home',
              }),
            );
        });
    },
    []
  );

  const renderQueryCard = useCallback((userQuery) => {
    return (
      <AppCard
        className="mt-4 mb-4"
        style={{
          width: '100%',
          backgroundColor: '#eaf3f7',
        }}
      >
        <div className="mb-3">
          <h3 className="m-0">{userQuery.subject}</h3>
        </div>
        <p
          style={{
            fontSize: '20px',
            whiteSpace: 'pre-wrap',
          }}
        >
          {userQuery?.messages[0]?.message}
        </p>
        {userQuery?.messages[0]?.files?.length > 0 && (
          <div>
            <hr className="my-2 border-top border-light" />
            {userQuery?.messages[0]?.files?.map((f, i) => (
              <span
                key={i}
                title={f.name}
                style={{ fontSize: '20px' }}
                className="pointer mr-1"
                onClick={() =>
                  handleDownloadFile(userQuery?.messages[0]?.id, f.key, f.name)
                }
              >
                {minions.files.fileIcon(f.name, true, null)}
              </span>
            ))}
          </div>
        )}
        <hr className="my-2 border-top border-light" />
        <span className="d-block text-right">
          {userQuery.dateStamp}, {userQuery.user}
        </span>
      </AppCard>
    );
  });

  const getReplied = useCallback((message) => {
    if (message.admin) return message.admin;
    if (message.user) return message.user;

    return 'Robot Worldlex';
  });

  const renderCard = useCallback((message, index) => {
    return (
      <AppCard
        className={`mt-4 mb-4 ${message.admin || index > 4 ? '' : 'ml-auto'}`}
        style={{
          width: '80%',
          backgroundColor:
            message.admin && userQueryThread.messages.length - 1 === index + 1
              ? '#d7f7da'
              : '',
        }}
        footer={
          message.admin &&
          userQueryThread.messages.length - 1 === index + 1 &&
          authUser && (
            <div className="d-flex justify-content-between">
              <div>
                {index === 4 ? (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => handleReply(null, true)}
                  >
                    <Translator
                      file="module-legalCompliance"
                      name="m-gcl-need.consultant.contact.me"
                    />
                  </button>
                ) : (
                  index < 6 && (
                    <button
                      type="button"
                      className="btn btn-outline-info"
                      onClick={() => setShowEditModal(true)}
                    >
                      <Translator
                        file="module-legalCompliance"
                        name="m-gcl-reopen.consultation"
                      />
                    </button>
                  )
                )}
              </div>
              {/* <div className="d-flex align-items-center">
                <span>Valore la respuesta:</span>
                <AppScore
                  className="ml-2"
                  value={message.score}
                  onClick={(v) => handleSetScore(v, message.id)}
                />
              </div> */}
            </div>
          )
        }
      >
        <div className="mb-3">
          <h3 className="m-0">
            {message.admin ? (
              <Translator
                file="module-legalCompliance"
                name="m-gcl-response.from.worldlex"
              />
            ) : (
              ''
            )}
          </h3>
        </div>
        <p
          style={{ fontSize: '20px' }}
          dangerouslySetInnerHTML={{
            __html: message.message,
          }}
        />
        {message?.files.length > 0 && (
          <div>
            <hr className="my-2 border-top border-light" />
            {message?.files?.map((f, i) => (
              <span
                key={i}
                title={f.name}
                style={{ fontSize: '20px' }}
                className="pointer mr-1"
                onClick={() => handleDownloadFile(message?.id, f.key, f.name)}
              >
                {minions.files.fileIcon(f.name, true, null)}
              </span>
            ))}
          </div>
        )}
        <hr className="my-2 border-top border-light" />
        <span className="d-block text-right">
          {message.dateStamp}, {getReplied(message)}
        </span>
      </AppCard>
    );
  });

  return (
    <>
      <Sidebar>
        <img
          src={client?.logoUrl}
          className="img-fluid"
          alt={client?.name}
          title={client?.name}
        />
        <Accordion>
          <ul>
            <li>
              <AppLink
                path="user_module_legalCompliance_home"
                params={{ clientID: client.id, moduleBridgeHex }}
                className="sidebar-element"
              >
                <AppRemixIcon
                  icon="arrow-go-back-line"
                  className="sidebar-icon"
                  noVariants
                />{' '}
                <span className="sb-title">
                  <Translator file="module-legalCompliance" name="go.back" />
                </span>
              </AppLink>
              <AppLink
                path="user_module_legalCompliance_evaluation"
                params={{
                  clientID: client.id,
                  moduleBridgeHex,
                  plantHex,
                  page: 1,
                  filtersHex: 0,
                }}
                className="sidebar-element"
              >
                <AppRemixIcon icon="check-double" className="sidebar-icon" />{' '}
                <span className="sb-title">
                  <Translator
                    file="module-legalCompliance"
                    name="go.to.evaluation"
                  />
                </span>
              </AppLink>
            </li>
          </ul>
        </Accordion>
      </Sidebar>

      <BreadcrumbHandler
        paths={
          userQueryThread
            ? userQueryThread?.plantName
              ? [
                  {
                    name: userQueryThread.plantName,
                    route: {
                      name: 'user_module_legalCompliance_evaluation',
                      params: {
                        moduleBridgeHex,
                        plantHex,
                        page: 1,
                        filtersHex: 0,
                      },
                    },
                  },
                  {
                    name: ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="m-gcl-consult"
                      />,
                    ),
                    route: {
                      name: 'user_module_legalCompliance_evaluation',
                      params: {
                        moduleBridgeHex,
                        plantHex,
                      },
                    },
                  },
                  {
                    name: `${(
                      <Translator
                        file="module-legalCompliance"
                        name="m-gcl-consultation"
                      />
                    )} ${userQueryThreadHEX}`,
                  },
                ]
              : [
                  {
                    name: ReactDOMServer.renderToString(
                      <Translator
                        file="module-legalCompliance"
                        name="m-gcl-consult"
                      />,
                    ),
                    route: {
                      name: 'user_module_legalCompliance_evaluation',
                      params: {
                        moduleBridgeHex,
                        plantHex,
                      },
                    },
                  },
                  {
                    name: `${(
                      <Translator
                        file="module-legalCompliance"
                        name="m-gcl-consultation"
                      />
                    )} ${userQueryThreadHEX}`,
                  },
                ]
            : [{ name: '...' }]
        }
      />

      {userQueryThread ? (
        <div className="p-4">
          <div>{renderQueryCard(userQueryThread)}</div>
          {userQueryThread.messages
            .filter((_, i) => i >= 1)
            .map((message, i) => (
              <div key={message.id}>{renderCard(message, i)}</div>
            ))}
        </div>
      ) : (
        <AppNoInfo>
          <SpinnerLoader />
        </AppNoInfo>
      )}

      <AppModal
        show={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={ReactDOMServer.renderToString(
          <Translator
            file="module-legalCompliance"
            name="m-gcl-reply.message.from.worldlex"
          />,
        )}
        size="xl"
      >
        <Formik
          initialValues={{
            message: '',
            file: null,
          }}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={(v) => handleReply(v)}
        >
          {({ handleSubmit, values, setFieldValue, errors, isSubmitting }) => (
            <>
              <div className="row">
                <div className="col-lg-12">
                  <AppTextArea
                    title={
                      <Translator
                        file="module-legalCompliance"
                        name="module.lc.plant-info.request.message"
                      />
                    }
                    icon={<AppRemixIcon icon="mail" />}
                    onChange={(opt) => setFieldValue('message', opt)}
                    value={values.message}
                    error={errors.message}
                  />
                </div>
                <div className="col-lg-12">
                  <AppFileUploader
                    placeholder={
                      values.file ? (
                        values.file[0].name
                      ) : (
                        <Translator file="general" name="select.a.file" />
                      )
                    }
                    onChange={(opt) => setFieldValue('file', opt)}
                    btnText={<Translator file="general" name="search..." />}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12 mt-4">
                  <span
                    className="btn btn-outline-light"
                    onClick={() => setShowEditModal(false)}
                  >
                    <Translator file="user" name="user.cancel" />
                  </span>
                  <span
                    className={`btn btn-success float-right ${
                      isSubmitting ? 'disabled' : ''
                    }`}
                    onClick={handleSubmit}
                  >
                    <Translator
                      file="module-legalCompliance"
                      name="module.lc.plant-info.request.send"
                    />
                  </span>
                </div>
              </div>
            </>
          )}
        </Formik>
      </AppModal>
    </>
  );
}
